import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, ApiError, type OdrConfig, type OdrState } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Alert, Button, PageTitle, Panel, Select, Switch } from "../components/ui";
import {
  formatHours, hoursDecimals, isRounding, reportRuns,
  type RoundingMode, type RoundingRule, type RoundingScope,
} from "../lib/rounding";

/**
 * An organisation's ODR: what its iPads log, and how its sheets report hours.
 *
 * Edits save to a draft as they are made. Nothing reaches an iPad or a sheet
 * until it is published, and publishing makes a numbered version that never
 * changes — missions already flown keep the settings they were flown under.
 */

const MODES: { value: RoundingMode; label: string }[] = [
  { value: "none", label: "Exact, to the minute" },
  { value: "up", label: "Round up" },
  { value: "nearest", label: "Round to nearest" },
  { value: "down", label: "Round down" },
];

const SCOPES: { value: RoundingScope; label: string }[] = [
  { value: "perLeg", label: "Each leg, then add them up" },
  { value: "total", label: "The total only" },
];

const INCREMENTS = [
  { minutes: 6, label: "0.1 hour (6 min)" },
  { minutes: 15, label: "0.25 hour (15 min)" },
  { minutes: 30, label: "0.5 hour (30 min)" },
  { minutes: 60, label: "1 hour" },
];

/** Two runs to show a rule on. Dylan's own example: the sum of rounded legs differs from the rounded sum. */
const SAMPLE_MINUTES = [122, 42];

const same = (a: OdrConfig, b: OdrConfig) => JSON.stringify(a) === JSON.stringify(b);

function starting(state: OdrState): OdrConfig {
  return state.draft?.config ?? state.published?.config ?? state.defaults;
}

function Row({
  title, children, control,
}: { title: string; children?: React.ReactNode; control: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 px-5 py-4">
      <div className="min-w-0">
        <p className="text-[1rem] font-semibold text-ink">{title}</p>
        {children && <p className="mt-0.5 text-[0.9rem] text-ink-soft leading-relaxed">{children}</p>}
      </div>
      {control}
    </div>
  );
}

function Hours({
  label, rule, onChange, disabled,
}: { label: string; rule: RoundingRule; onChange: (r: RoundingRule) => void; disabled: boolean }) {
  const active = isRounding(rule);
  const sample = reportRuns(SAMPLE_MINUTES.map((m) => m * 60), rule);
  const dp = hoursDecimals(rule);
  const presets = INCREMENTS.some((i) => i.minutes === rule.incrementMinutes)
    ? INCREMENTS
    : [...INCREMENTS, { minutes: rule.incrementMinutes, label: `${rule.incrementMinutes} min` }];

  return (
    <Panel>
      <Row
        title={label}
        control={
          <Select
            aria-label={`${label} rounding`}
            value={rule.mode}
            disabled={disabled}
            onChange={(e) => onChange({ ...rule, mode: e.target.value as RoundingMode })}
            className="min-w-52"
          >
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
        }
      />
      {active && (
        <>
          <div className="border-t border-rule-soft">
            <Row
              title="To the next"
              control={
                <Select
                  aria-label={`${label} increment`}
                  value={rule.incrementMinutes}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...rule, incrementMinutes: Number(e.target.value) })}
                  className="min-w-52"
                >
                  {presets.map((i) => <option key={i.minutes} value={i.minutes}>{i.label}</option>)}
                </Select>
              }
            />
          </div>
          <div className="border-t border-rule-soft">
            <Row
              title="Applied to"
              control={
                <Select
                  aria-label={`${label} scope`}
                  value={rule.scope}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...rule, scope: e.target.value as RoundingScope })}
                  className="min-w-52"
                >
                  {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Select>
              }
            />
          </div>
          <p className="border-t border-rule-soft px-5 py-3.5 text-[0.9rem] text-ink-soft tabular-nums">
            {SAMPLE_MINUTES.join(" min and ")} min report as{" "}
            <span className="font-semibold text-ink">
              {sample.perRun.map((m) => formatHours(m, dp)).join(" h and ")} h
            </span>
            , a total of <span className="font-semibold text-ink">{formatHours(sample.total, dp)} h</span>.
          </p>
        </>
      )}
    </Panel>
  );
}

export function Odr() {
  const auth = useAuth();
  const [state, setState] = useState<OdrState | null>(null);
  const [config, setConfig] = useState<OdrConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Whether the editor is showing something the server has not seen yet.
  const touched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api.getOdr().then(
      (s) => {
        if (cancelled) return;
        setState(s);
        setConfig(starting(s));
      },
      (err) => !cancelled && setError(err instanceof ApiError ? err.message : "Couldn't load the ODR."),
    );
    return () => { cancelled = true; };
  }, []);

  const isAdmin = auth.status === "authenticated" && auth.me.user.role === "admin";

  // Saves the draft shortly after the last change. A debounce, so dragging
  // through a select does not send a request per option.
  useEffect(() => {
    if (!config || !touched.current || !isAdmin) return;
    setSaveState("saving");
    const timer = window.setTimeout(() => {
      api.saveOdrDraft(config).then(
        () => { setSaveState("saved"); setError(null); },
        (err) => {
          setSaveState("idle");
          setError(err instanceof ApiError ? err.message : "Couldn't save your changes.");
        },
      );
    }, 600);
    return () => window.clearTimeout(timer);
  }, [config, isAdmin]);

  if (error && !state) return <Alert>{error}</Alert>;
  if (!state || !config) return <p className="text-[0.95rem] text-ink-soft">Loading…</p>;

  const published = state.published;
  const nextVersion = (published?.version ?? 0) + 1;
  // With nothing published, publishing is how an organisation takes over its
  // iPads' settings — so it is allowed even when nothing differs from default.
  const canPublish = isAdmin && (published === null || !same(config, published.config));

  const edit = (next: OdrConfig) => {
    touched.current = true;
    setConfirming(false);
    setConfig(next);
  };

  async function publish() {
    setPublishing(true);
    setError(null);
    try {
      await api.saveOdrDraft(config!);
      await api.publishOdr();
      const fresh = await api.getOdr();
      touched.current = false;
      setState(fresh);
      setConfig(starting(fresh));
      setSaveState("idle");
      setConfirming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't publish.");
    } finally {
      setPublishing(false);
    }
  }

  const status = published
    ? `Version ${published.version} is live${canPublish ? " · changes not published" : ""}`
    : "Not published";

  return (
    <div className="flex flex-col gap-8 max-w-2xl">
      <div>
        <Link
          to="/app/settings"
          className="inline-flex items-center gap-1 text-[1rem] text-accent-text hover:underline underline-offset-4 mb-3"
        >
          <ArrowLeft size={18} strokeWidth={2.4} aria-hidden />
          Settings
        </Link>
        <PageTitle detail={status}>ODR</PageTitle>
      </div>

      {error && <Alert>{error}</Alert>}
      {!isAdmin && <p className="px-1 text-[0.95rem] text-ink-soft">Only an admin can change the ODR.</p>}

      <div>
        <p className="section-label mb-2.5 px-1">On the iPad</p>
        <Panel className="divide-y divide-rule-soft">
          <Row
            title="Individual leg fuels"
            control={
              <Switch
                label="Individual leg fuels"
                checked={config.ipad.fuelPrompts.atEngineShutdown}
                disabled={!isAdmin}
                // The app has one switch and sets both flags together; so does this.
                onChange={(v) =>
                  edit({
                    ...config,
                    ipad: { ...config.ipad, fuelPrompts: { atEngineStart: v, atEngineShutdown: v } },
                  })
                }
              />
            }
          >
            Asks for the fuel level at every shutdown, so each leg has its own figures. A leg's starting fuel carries over
            from the previous shutdown plus any refuel. The first start of a mission always asks. Adds Fuel On and Fuel
            Off columns to the sheet.
          </Row>
          <Row
            title="Cooldown timer"
            control={
              <Switch
                label="Cooldown timer"
                checked={config.ipad.showCooldownTimer}
                disabled={!isAdmin}
                onChange={(v) => edit({ ...config, ipad: { ...config.ipad, showCooldownTimer: v } })}
              />
            }
          >
            Shows a Cooldown button before Shutdown. It counts up from zero and counts as engine time.
          </Row>
        </Panel>
        <p className="mt-2.5 px-1 text-[0.9rem] text-ink-soft leading-relaxed">
          {published
            ? "iPads signed in to your organisation follow these settings, not the switches in the app."
            : "Until you publish, each iPad uses the settings in its own app."}
        </p>
      </div>

      <div>
        <p className="section-label mb-2.5 px-1">Reported hours</p>
        <div className="flex flex-col gap-4">
          <Hours
            label="Engine time"
            rule={config.report.engineTime}
            disabled={!isAdmin}
            onChange={(r) => edit({ ...config, report: { ...config.report, engineTime: r } })}
          />
          <Hours
            label="Flight time"
            rule={config.report.flightTime}
            disabled={!isAdmin}
            onChange={(r) => edit({ ...config, report: { ...config.report, flightTime: r } })}
          />
        </div>
        <p className="mt-2.5 px-1 text-[0.9rem] text-ink-soft leading-relaxed">
          Changes how hours are reported on sheets and screens. The exact time is always kept, and
          fuel burn is worked out from it.
        </p>
      </div>

      {isAdmin && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[0.95rem] text-ink-soft">
            {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Draft saved" : ""}
          </p>

          {confirming ? (
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <p className="text-[0.9rem] text-ink-soft max-w-xs text-right">
                New missions use version {nextVersion}. Missions already started keep their settings.
              </p>
              <Button variant="secondary" onClick={() => setConfirming(false)} disabled={publishing}>
                Cancel
              </Button>
              <Button onClick={() => void publish()} busy={publishing}>
                Publish
              </Button>
            </div>
          ) : (
            <Button onClick={() => setConfirming(true)} disabled={!canPublish}>
              Publish version {nextVersion}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
